import React, { useRef, useState, useEffect } from 'react';
import { FaClock, FaCopy, FaTrash, FaQuestionCircle, FaCloud, FaCog } from "react-icons/fa";
import { MdFeedback } from 'react-icons/md';
import './SuppliersDetailPage.css';

// Date utility functions
const getCurrentDate = () => {
    const now = new Date();
    const day = String(now.getDate()).padStart(2, '0');
    const month = String(now.getMonth() + 1).padStart(2, '0');
    const year = now.getFullYear();
    return `${day}-${month}-${year}`;
};

const formatDateToDDMMYYYY = (dateString) => {
    if (!dateString) return getCurrentDate();

    // If already in DD-MM-YYYY format, return as is
    if (typeof dateString === 'string' && dateString.includes('-')) {
        const parts = dateString.split('-');
        if (parts.length === 3 && parts[0].length === 2 && parts[1].length === 2 && parts[2].length === 4) {
            return dateString;
        }
    }

    // Parse the date and format to DD-MM-YYYY
    const date = new Date(dateString);
    if (isNaN(date.getTime())) return getCurrentDate();

    const day = String(date.getDate()).padStart(2, '0');
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const year = date.getFullYear();

    return `${day}-${month}-${year}`;
};

const isValidDDMMYYYY = (dateString) => {
    if (!dateString) return false;

    const regex = /^(\d{2})-(\d{2})-(\d{4})$/;
    if (!regex.test(dateString)) return false;

    const [, day, month, year] = dateString.match(regex);
    const dayNum = parseInt(day, 10);
    const monthNum = parseInt(month, 10);
    const yearNum = parseInt(year, 10);

    if (monthNum < 1 || monthNum > 12) return false;
    if (dayNum < 1 || dayNum > 31) return false;

    // Basic date validation
    const date = new Date(yearNum, monthNum - 1, dayNum);
    return date.getDate() === dayNum &&
           date.getMonth() === monthNum - 1 &&
           date.getFullYear() === yearNum;
};

const CreateExpensePopup = ({
    showCreateExpensePopup,
    setShowCreateExpensePopup,
    suppliers,
    payee,
    setPayee,
    paymentAccount,
    setPaymentAccount,
    paymentDate,
    setPaymentDate,
    paymentMethod,
    setPaymentMethod,
    description,
    setDescription,
    expenseFile,
    setExpenseFile,
    tableData,
    setTableData,
    totalAmount,
    handleExpenseCreated,
    handleSaveAndNew,
    handleClear,
    handlePrint,
    handleRecurring,
    handleAddRow,
    handleDeleteRow,
    handleCopyRow,
    handleUpdateRow,
    handleClearAllLines,
    handleFileChange,
    selectedExpense,
    categories,
}) => {
    const createPopupRef = useRef(null);
    const fileInputRef = useRef(null);
    const [categoryMap, setCategoryMap] = useState({});
    const [loadingCategories, setLoadingCategories] = useState(false);
    const [error, setError] = useState(null);
    const [expenseNumber, setExpenseNumber] = useState('');
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [dateError, setDateError] = useState('');

    const API_BASE_URL = "https://backend.hira-tech.co.uk";

    const forceExpenseNumber = (expenseData) => {
        if (!expenseNumber || !expenseNumber.trim()) {
            throw new Error("Expense number cannot be empty");
        }

        const forcedData = {
            ...expenseData,
            expense_number: expenseNumber.trim(),
            no: expenseNumber.trim(),
            reference_number: expenseNumber.trim(),
            ref_no: expenseNumber.trim(),
            _forceExpenseNumber: true,
            _manualEntry: true,
            _preventAutoGenerate: true
        };

        console.log("🔧 FORCING MANUAL expense_number:", forcedData.expense_number);
        console.log("🛡️ All protection flags set");
        return forcedData;
    };

    useEffect(() => {
        console.log("🔍 expenseNumber changed:", expenseNumber);
    }, [expenseNumber]);

    useEffect(() => {
        console.log("🔄 Sync effect - selectedExpense:", selectedExpense);
        if (selectedExpense) {
            const refNo = selectedExpense.no || selectedExpense.expense_number || selectedExpense.reference_number || selectedExpense.ref_no || '';
            console.log("📝 Setting expenseNumber from selectedExpense:", refNo);
            setExpenseNumber(refNo);

            // Format the date to DD-MM-YYYY when editing an expense
            if (selectedExpense.date) {
                const formattedDate = formatDateToDDMMYYYY(selectedExpense.date);
                console.log("📅 Setting paymentDate from selectedExpense:", formattedDate);
                setPaymentDate(formattedDate);
            }
        } else {
            console.log("🆕 Creating new expense - setting empty expenseNumber and current date");
            setExpenseNumber('');
            setPaymentDate(getCurrentDate());
        }
    }, [selectedExpense, showCreateExpensePopup]);

    useEffect(() => {
        setLoadingCategories(true);
        fetch(`${API_BASE_URL}/api/categories/`, {
            headers: { Accept: "application/json" },
        })
            .then((response) => {
                if (!response.ok) throw new Error(`Categories fetch error! Status: ${response.status}`);
                return response.json();
            })
            .then((data) => {
                const map = data.reduce((acc, cat) => {
                    acc[cat.name] = cat.display_name;
                    return acc;
                }, {});
                setCategoryMap(map);
                setError(null);
            })
            .catch((error) => {
                console.error(`Failed to fetch categories: ${error.message}`);
                setError(`Failed to fetch categories: ${error.message}`);
            })
            .finally(() => setLoadingCategories(false));
    }, []);

    useEffect(() => {
        const handleClickOutside = (event) => {
            if (createPopupRef.current && !createPopupRef.current.contains(event.target)) {
                setShowCreateExpensePopup(false);
            }
        };
        document.addEventListener("mousedown", handleClickOutside);
        return () => document.removeEventListener("mousedown", handleClickOutside);
    }, [setShowCreateExpensePopup]);

    const resetForm = () => {
        console.log("🔄 Resetting form - setting empty expenseNumber and current date");
        setExpenseNumber('');
        setPayee('');
        setPaymentAccount('Cash on hand');
        setPaymentDate(getCurrentDate());
        setPaymentMethod('');
        setDescription('');
        setExpenseFile(null);
        setTableData([{ id: Date.now(), rowNumber: "1", category: "", description: "", totalAmount: "" }]);
        setDateError('');
    };

    const validateForm = () => {
        if (!expenseNumber || !expenseNumber.trim()) {
            alert("Please enter a reference number.");
            return false;
        }

        // Validate date format
        if (!isValidDDMMYYYY(paymentDate)) {
            setDateError("Please enter a valid date in DD-MM-YYYY format");
            return false;
        }

        for (const [index, item] of tableData.entries()) {
            if (!item.category) {
                alert(`Please select a category for item ${index + 1}.`);
                return false;
            }
            if (!item.description || !item.description.trim()) {
                alert(`Please enter a description for item ${index + 1}.`);
                return false;
            }
            if (!item.totalAmount || item.totalAmount.trim() === "") {
                alert(`Please enter an amount for item ${index + 1}.`);
                return false;
            }
            const amount = parseFloat(item.totalAmount);
            if (isNaN(amount) || amount <= 0) {
                alert(`Please enter a valid positive amount for item ${index + 1} (e.g., 34 or 34.54).`);
                return false;
            }
        }
        return true;
    };

    const prepareExpenseData = () => {
        const validItems = tableData.map(item => ({
            rowNumber: item.rowNumber || "1",
            category: item.category || "General",
            description: item.description || "",
            totalAmount: item.totalAmount ? parseFloat(item.totalAmount) : 0,
        }));
        const total = parseFloat(validItems.reduce((sum, item) => sum + (item.totalAmount || 0), 0).toFixed(2));
        const salesTax = total > 0 ? (total * 0.17 / 1.17).toFixed(2) : "0.00";

        return {
            id: selectedExpense ? selectedExpense.id : undefined,
            date: paymentDate || getCurrentDate(), // Now in DD-MM-YYYY format
            expense_number: expenseNumber.trim(),
            supplier: payee || null,
            method: paymentMethod || "Cash",
            terms: validItems[0]?.category || "General",
            memo: description || "",
            sales_tax: parseFloat(salesTax),
            file: expenseFile ? expenseFile : null,
            items: validItems,
            status: selectedExpense ? selectedExpense.status : "Pending",
            _manualExpenseNumber: true,
            _preventAutoGeneration: true
        };
    };

    const handleFormSubmit = async (expenseData, isSaveAndNew = false) => {
        setIsSubmitting(true);
        try {
            console.log("🚀 SUBMITTING EXPENSE - MANUAL ENTRY ENFORCED");
            console.log("📝 expenseNumber from state:", expenseNumber);
            console.log("📅 paymentDate from state:", paymentDate);
            console.log("📦 Full expense data being sent:", expenseData);

            if (!expenseNumber || !expenseNumber.trim()) {
                throw new Error("Expense number is required. Please enter a reference number.");
            }

            const forcedData = forceExpenseNumber(expenseData);
            console.log("🛡️ After forcing - expense_number:", forcedData.expense_number);
            console.log("🔒 Data being passed to parent:", forcedData);

            if (isSaveAndNew) {
                console.log("💾 Calling handleSaveAndNew with manually entered data");
                await handleSaveAndNew(forcedData);
                resetForm();
            } else {
                console.log("💾 Calling handleExpenseCreated with manually entered data");
                await handleExpenseCreated(forcedData);
                if (!selectedExpense) {
                    resetForm();
                }
            }
            setError(null);
            setDateError('');
        } catch (err) {
            console.error("❌ Error:", err);
            setError(`Failed to save expense: ${err.message}`);
        } finally {
            setIsSubmitting(false);
        }
    };

    const handleDateChange = (e) => {
        const value = e.target.value;
        setDateError('');

        // Allow only numbers and dashes
        if (value === '' || /^[\d-]*$/.test(value)) {
            setPaymentDate(value);

            // Validate format in real-time
            if (value.length === 10 && !isValidDDMMYYYY(value)) {
                setDateError("Please use DD-MM-YYYY format (e.g., 25-12-2023)");
            }
        }
    };

    const handleDateBlur = () => {
        if (paymentDate && !isValidDDMMYYYY(paymentDate)) {
            setDateError("Please enter a valid date in DD-MM-YYYY format");
        } else {
            setDateError('');
        }
    };

    return (
        showCreateExpensePopup && (
            <div className="upload-popup-overlay">
                <div className="create-bill-popup" ref={createPopupRef} onClick={(e) => e.stopPropagation()}>
                    {error && <div className="error-message" style={{ color: 'red', marginBottom: '1rem' }}>Error: {error}</div>}
                    {loadingCategories && <div>Loading categories...</div>}
                    <div className="bill-form-header">
                        <div className="header-title">
                            <FaClock className="header-icon" />
                            <span>{selectedExpense ? "Edit Expense" : "New Expense"}</span>
                        </div>
                        <div className="header-actions">
                            <button type="button" className="icon-btn" onClick={() => alert("Copy expense not implemented")}>
                                <FaCopy />
                                <span>Copy</span>
                            </button>
                            <button type="button" className="text-icon-btn">
                                <MdFeedback />
                                <span>Give Feedback</span>
                            </button>
                            <button type="button" className="icon-btn">
                                <FaQuestionCircle />
                            </button>
                            <button className="close-button" onClick={() => setShowCreateExpensePopup(false)}>×</button>
                        </div>
                    </div>
                    <h2 className="form-main-title">{selectedExpense ? "Edit Expense" : "New Expense"}</h2>
                    <form
                        onSubmit={async (e) => {
                            e.preventDefault();
                            if (!validateForm()) return;

                            const expenseData = prepareExpenseData();
                            await handleFormSubmit(expenseData, false);
                        }}
                        className="bill-form-container"
                    >
                        <div className="form-section">
                            <div className="form-row">
                                <div className="form-group wide-group">
                                    <label>Payee</label>
                                    <select
                                        value={payee}
                                        onChange={(e) => setPayee(e.target.value)}
                                        style={{ width: "300px" }}
                                    >
                                        <option value="">Select Payee (Optional)</option>
                                        {suppliers.length === 0 ? (
                                            <option disabled>No suppliers found 😵</option>
                                        ) : (
                                            suppliers.map((supplier) => (
                                                <option key={supplier.id} value={supplier.supplier_display_name}>
                                                    {supplier.supplier_display_name}
                                                </option>
                                            ))
                                        )}
                                    </select>
                                </div>
                                <div className="form-group amount-display">
                                    <label>Balance Due</label>
                                    <p style={{ fontWeight: "bold", color: "#333", fontSize: "25px" }}>
                                        £{totalAmount.toFixed(2)}
                                    </p>
                                </div>
                            </div>
                            <div className="form-row five-items-row">
                                <div className="form-group">
                                    <label>Payment account</label>
                                    <select
                                        value={paymentAccount}
                                        onChange={(e) => setPaymentAccount(e.target.value)}
                                        style={{ width: "200px" }}
                                    >
                                        <option value="Cash on hand">Cash on hand</option>
                                        <option value="Bank">Bank</option>
                                        <option value="Credit Card">Credit Card</option>
                                    </select>
                                </div>
                                <div className="form-group" style={{ flex: 1, width: "200px", marginLeft: "-80px" }}>
                                    <label>Payment Date <span style={{ color: 'red' }}>*</span></label>
                                    <input
                                        type="text"
                                        value={paymentDate}
                                        onChange={handleDateChange}
                                        onBlur={handleDateBlur}
                                        style={{
                                            width: "200px",
                                            border: dateError ? '1px solid red' : '1px solid #ddd'
                                        }}
                                        placeholder="DD-MM-YYYY"
                                        maxLength={10}
                                    />
                                    {dateError && (
                                        <small style={{ color: 'red', fontSize: '12px', display: 'block' }}>
                                            {dateError}
                                        </small>
                                    )}
                                    <small style={{ color: '#666', fontSize: '12px', display: 'block', marginTop: '5px' }}>
                                        Format: DD-MM-YYYY
                                    </small>
                                </div>
                                <div className="form-group" style={{ flex: 1, width: "200px", marginLeft: "-160px" }}>
                                    <label>Payment Method</label>
                                    <select
                                        value={paymentMethod}
                                        onChange={(e) => setPaymentMethod(e.target.value)}
                                        style={{ width: "200px" }}
                                    >
                                        <option value="">Select method</option>
                                        <option value="Cash">Cash</option>
                                        <option value="Card">Card</option>
                                        <option value="Bank Transfer">Bank Transfer</option>
                                    </select>
                                </div>
                                <div className="form-group" style={{ width: "200px", marginLeft: "-240px" }}>
                                    <label>Ref. no. <span style={{ color: 'red' }}>*</span></label>
                                    <input
                                        type="text"
                                        value={expenseNumber}
                                        onChange={(e) => setExpenseNumber(e.target.value)}
                                        style={{ width: "200px" }}
                                        required
                                        placeholder="Enter expense number"
                                    />
                                </div>
                            </div>
                            <div className="table-section">
                                <div className="table-header">
                                    <span>Items</span>
                                    <button
                                        type="button"
                                        className="settings-btn"
                                        onClick={() => alert("Settings not implemented yet")}
                                    >
                                        <FaCog />
                                    </button>
                                </div>
                                <div className="table-container">
                                    <table className="item-table">
                                        <thead>
                                            <tr>
                                                <th>#</th>
                                                <th>Category</th>
                                                <th>Description</th>
                                                <th>Amount</th>
                                                <th></th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {tableData.map((row) => (
                                                <tr key={row.id}>
                                                    <td>{row.rowNumber}</td>
                                                    <td>
                                                        <select
                                                            value={row.category}
                                                            onChange={(e) => handleUpdateRow(row.id, "category", e.target.value)}
                                                        >
                                                            {categories.length === 0 ? (
                                                                <option disabled>No categories found</option>
                                                            ) : (
                                                                <>
                                                                    <option value="">Select category</option>
                                                                    {categories.map((cat) => (
                                                                        <option key={cat.name} value={cat.name}>
                                                                            {cat.display_name}
                                                                        </option>
                                                                    ))}
                                                                </>
                                                            )}
                                                        </select>
                                                    </td>
                                                    <td>
                                                        <input
                                                            type="text"
                                                            value={row.description}
                                                            onChange={(e) => handleUpdateRow(row.id, "description", e.target.value)}
                                                            placeholder="Enter description"
                                                        />
                                                    </td>
                                                    <td>
                                                        <input
                                                            type="text"
                                                            value={row.totalAmount}
                                                            onChange={(e) => {
                                                                const value = e.target.value;
                                                                if (value === '' || /^\d*\.?\d*$/.test(value)) {
                                                                    handleUpdateRow(row.id, "totalAmount", value);
                                                                }
                                                            }}
                                                            placeholder="Enter amount (e.g., 34 or 34.54)"
                                                        />
                                                    </td>
                                                    <td className="action-buttons">
                                                        <button
                                                            type="button"
                                                            className="icon-button copy"
                                                            onClick={() => handleCopyRow(row.id)}
                                                            title="Copy"
                                                        >
                                                            <FaCopy />
                                                        </button>
                                                        <button
                                                            type="button"
                                                            className="icon-button delete"
                                                            onClick={() => handleDeleteRow(row.id)}
                                                            title="Delete"
                                                        >
                                                            <FaTrash />
                                                        </button>
                                                    </td>
                                                </tr>
                                            ))}
                                            <tr>
                                                <td colSpan="5"></td>
                                            </tr>
                                        </tbody>
                                    </table>
                                </div>
                                <div style={{ display: "flex", gap: "1rem", marginTop: "1rem", alignItems: "flex-start", marginTop: "-10px" }}>
                                    <button
                                        type="button"
                                        className="add-button"
                                        onClick={handleAddRow}
                                        title="Add Row"
                                    >
                                        Add Lines
                                    </button>
                                    <button
                                        type="button"
                                        className="add-secondary-button"
                                        onClick={handleClearAllLines}
                                        title="Clear Lines"
                                    >
                                        Clear all Lines
                                    </button>
                                </div>
                                <div style={{ display: "flex", alignItems: "right", marginLeft: "1280px", marginTop: "-50px" }}>
                                    <label style={{ fontSize: "12px", fontWeight: "bold" }}>Total Amount</label>
                                    <p style={{ fontWeight: "bold", color: "#333", fontSize: "12px" }}>
                                        : £{totalAmount.toFixed(2)}
                                    </p>
                                </div>
                                <div style={{ display: "flex", gap: "0.5rem", marginTop: "1rem", alignItems: "flex-start" }}>
                                    <div className="form-group" style={{ flex: 1, marginBottom: "-50px", marginTop: "30px" }}>
                                        <label>Memo/Description</label>
                                        <textarea
                                            value={description}
                                            onChange={(e) => setDescription(e.target.value)}
                                            placeholder="Enter memo or description"
                                            style={{
                                                width: "100%",
                                                height: "100px",
                                                padding: "0.7rem",
                                                border: "1px solid #ddd",
                                                borderRadius: "4px",
                                                resize: "vertical",
                                            }}
                                        />
                                    </div>
                                    <div className="form-group" style={{ marginTop: "1rem" }}>
                                        <label>Expense PDF (Optional)</label>
                                        <div
                                            className="upload-area"
                                            style={{
                                                padding: "0.5rem",
                                                height: "100px",
                                                marginTop: "15px",
                                                width: "100%",
                                                textAlign: "center",
                                                cursor: "pointer",
                                                display: "flex",
                                                flexDirection: "column",
                                                justifyContent: "center",
                                                border: "1px solid #ddd",
                                                borderRadius: "4px",
                                            }}
                                            onClick={() => fileInputRef.current.click()}
                                        >
                                            <FaCloud size={20} style={{ marginBottom: "0.5rem", color: "#808080" }} />
                                            <p style={{ margin: "0", fontSize: "0.9rem" }}>
                                                Click to select PDF file
                                            </p>
                                            <input
                                                type="file"
                                                ref={fileInputRef}
                                                accept=".pdf"
                                                onChange={handleFileChange}
                                                style={{ display: "none" }}
                                            />
                                            {expenseFile && (
                                                <p
                                                    style={{
                                                        margin: "0.3rem 0",
                                                        color: "#333",
                                                        fontSize: "0.8rem",
                                                    }}
                                                >
                                                    Selected: {expenseFile.name}
                                                </p>
                                            )}
                                            {!expenseFile && (
                                                <p
                                                    style={{
                                                        margin: "0.3rem 0",
                                                        color: "#666",
                                                        fontSize: "0.8rem",
                                                    }}
                                                >
                                                    No file selected
                                                </p>
                                            )}
                                        </div>
                                    </div>
                                </div>
                                <div style={{ textAlign: "center", marginTop: "1rem" }}>
                                    <button
                                        type="button"
                                        style={{
                                            padding: "0.7rem 1.5rem",
                                            backgroundColor: "transparent",
                                            color: "#004aad",
                                            fontWeight: "bold",
                                            marginTop: "-10px",
                                            marginBottom: "-20px",
                                        }}
                                        onClick={() => alert("Privacy settings applied!")}
                                    >
                                        Privacy
                                    </button>
                                </div>
                            </div>
                        </div>
                        <div
                            className="form-action-buttons"
                            style={{
                                padding: "1rem",
                                backgroundColor: "#fff",
                                display: "flex",
                                justifyContent: "space-between",
                                alignItems: "center",
                                borderTop: "1px solid #ddd",
                                marginTop: "auto",
                            }}
                        >
                            <div>
                                <button
                                    type="button"
                                    className="cancel-btn"
                                    onClick={() => setShowCreateExpensePopup(false)}
                                    style={{ marginRight: "1rem" }}
                                >
                                    Cancel
                                </button>
                                <button
                                    type="button"
                                    className="clear-btn"
                                    onClick={handleClear}
                                    style={{ marginRight: "0" }}
                                >
                                    Clear
                                </button>
                            </div>
                            <div>
                                <button
                                    type="button"
                                    className="print-btn"
                                    onClick={handlePrint}
                                    style={{ marginRight: "1rem", borderRadius: "3px" }}
                                >
                                    Print
                                </button>
                                <button
                                    type="button"
                                    className="recurring-btn"
                                    onClick={handleRecurring}
                                    style={{ marginRight: "0" }}
                                >
                                    Recurring
                                </button>
                            </div>
                            <div>
                                <button
                                    type="submit"
                                    className="submit-btn"
                                    style={{ marginRight: "1rem" }}
                                    disabled={isSubmitting}
                                >
                                    {isSubmitting ? 'Saving...' : (selectedExpense ? "Update" : "Save")}
                                </button>
                                <button
                                    type="button"
                                    className="save-new-btn"
                                    onClick={async () => {
                                        if (!validateForm()) return;
                                        const expenseData = prepareExpenseData();
                                        await handleFormSubmit(expenseData, true);
                                    }}
                                    style={{ marginRight: "0" }}
                                    disabled={isSubmitting}
                                >
                                    {isSubmitting ? 'Saving...' : 'Save and New'}
                                </button>
                            </div>
                        </div>
                    </form>
                </div>
            </div>
        )
    );
};

export default CreateExpensePopup;